import { styled } from '@/stitches.config';
import { isMobile } from 'react-device-detect';
import { FullscreenButton } from './controls/FullscreenButton';
import { MuteUnmuteButton } from './controls/MuteUnmuteButton';
import { PlayPauseButton } from './controls/PlayPauseButton';
import { Scrubber } from './controls/Scrubber';
import { usePlayPauseOnIntersection } from './hooks/usePlayPauseOnIntersection';
import { useVideoStore } from './hooks/useVideoStore';

interface VideoControlsProps {
  autoplay: boolean;
}

export function VideoControls({ autoplay }: VideoControlsProps) {
  const { isLoaded, togglePlaying, isPlaying } = useVideoStore((state) => ({
    isLoaded: state.isLoaded,
    togglePlaying: state.togglePlaying,
    isPlaying: state.isPlaying,
  }));

  usePlayPauseOnIntersection(autoplay && !isMobile);

  function handleOnClick() {
    if (isMobile) return;
    togglePlaying();
  }

  return (
    <InteractiveContainer
      onClick={handleOnClick}
      css={{
        $$expandedControlsWidth: '0px',

        '&:hover': {
          $$expandedControlsWidth: isPlaying ? '18rem' : '0px',
        },
      }}
    >
      {/* Load controls only when the player is initialized */}
      {isLoaded && (
        <ControlBar onClick={(e) => e.stopPropagation()}>
          <PlayPauseButton />
          {!isMobile && (
            <ExpandedControlBar>
              <Scrubber />
              <MuteUnmuteButton />
              <FullscreenButton />
            </ExpandedControlBar>
          )}
        </ControlBar>
      )}
    </InteractiveContainer>
  );
}

const InteractiveContainer = styled('div', {
  position: 'absolute',
  inset: 0,
});

const ControlBar = styled('div', {
  position: 'absolute',
  bottom: '$0',
  left: '$0',
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: '$background',
  color: '$foreground',
  boxShadow: '1px 1px 4px 0px rgba(0, 0, 0, 0.2)',
  margin: '$4',

  '@bp2': {
    margin: '$0',
    bottom: '$5',
    left: '$5',
  },
});

const ExpandedControlBar = styled('div', {
  display: 'flex',
  alignItems: 'center',
  flex: 1,
  transition: 'all 0.5s ease-in-out',
  overflow: 'hidden',

  '@bp2': {
    width: '$$expandedControlsWidth',
    flex: 'unset',
  },
});
